const PA_DELIVERY_DISMISSED_KEY = 'pa:delivery-popup-dismissed-this-visit-v1';
const PA_DELIVERY_CLICK_COUNT_KEY = 'pa:delivery-popup-click-count-v1';

class PaDeliveryPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.closeButton = this.querySelector('[data-pa-delivery-close]');
    this.copyButton = this.querySelector('[data-pa-delivery-copy]');
    this.copyStatus = this.querySelector('[data-pa-delivery-copy-status]');
    if (!this.dialog || !this.closeButton) return;

    this.onCloseClick = this.dismiss.bind(this);
    this.onDialogClick = this.handleBackdropClick.bind(this);
    this.onCancel = this.handleCancel.bind(this);
    this.onInteraction = this.handleInteraction.bind(this);
    this.onCopy = this.copyCode.bind(this);
    this.popupTimer = null;
    this.closeButton.addEventListener('click', this.onCloseClick);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('cancel', this.onCancel);
    this.copyButton?.addEventListener('click', this.onCopy);

    if (this.querySelector('[data-pa-delivery-success]')) {
      this.open();
      this.querySelector('[data-pa-delivery-success]')?.focus();
      return;
    }

    if (this.querySelector('[data-pa-delivery-error]')) {
      this.open();
      return;
    }

    if (this.wasDismissedThisVisit()) return;

    document.addEventListener('click', this.onInteraction, { capture: true });
    const timeoutMs = Number(this.dataset.timeoutMs) || 10000;
    this.popupTimer = window.setTimeout(() => this.open(), timeoutMs);
  }

  disconnectedCallback() {
    this.clearPopupTimer();
    document.removeEventListener('click', this.onInteraction, { capture: true });
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    this.dialog?.removeEventListener('click', this.onDialogClick);
    this.dialog?.removeEventListener('cancel', this.onCancel);
    this.copyButton?.removeEventListener('click', this.onCopy);
  }

  handleInteraction(event) {
    if (!(event.target instanceof Element) || event.target.closest('pa-delivery-popup') || this.dialog.open) return;
    const clickThreshold = Number(this.dataset.clickThreshold) || 3;
    const clickCount = Number(this.readSession(PA_DELIVERY_CLICK_COUNT_KEY)) + 1;
    this.writeSession(PA_DELIVERY_CLICK_COUNT_KEY, String(clickCount));
    if (clickCount < clickThreshold) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open();
  }

  open() {
    if (this.dialog.open || this.wasDismissedThisVisit()) return;
    this.clearPopupTimer();
    document.removeEventListener('click', this.onInteraction, { capture: true });
    this.removeSession(PA_DELIVERY_CLICK_COUNT_KEY);
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
  }

  dismiss() {
    this.clearPopupTimer();
    this.writeSession(PA_DELIVERY_DISMISSED_KEY, 'true');
    this.dialog.close();
  }

  clearPopupTimer() {
    if (this.popupTimer === null) return;
    window.clearTimeout(this.popupTimer);
    this.popupTimer = null;
  }

  handleBackdropClick(event) {
    if (event.target !== this.dialog) return;
    const bounds = this.dialog.getBoundingClientRect();
    const outside = event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
    if (outside) this.dismiss();
  }

  handleCancel(event) {
    event.preventDefault();
    this.dismiss();
  }

  wasDismissedThisVisit() {
    return this.readSession(PA_DELIVERY_DISMISSED_KEY) === 'true';
  }

  async copyCode() {
    const code = this.copyButton?.dataset.paDeliveryCopy;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      if (this.copyStatus) this.copyStatus.textContent = this.querySelector('[data-pa-delivery-success]')?.dataset.copySuccess || '';
    } catch {
      if (this.copyStatus) this.copyStatus.textContent = this.querySelector('[data-pa-delivery-success]')?.dataset.copyFallback || '';
    }
  }

  readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      return;
    }
  }

  removeSession(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      return;
    }
  }
}

if (!customElements.get('pa-delivery-popup')) {
  customElements.define('pa-delivery-popup', PaDeliveryPopup);
}
